import React, { Fragment, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Tooltip, Layout, Menu, Anchor, Button, Grid, MenuProps } from "antd";
import { MenuUnfoldOutlined } from "@ant-design/icons";
import styles from "./navbar.module.scss";

// redux & query
import { useAppDispatch, useAppSelector } from "store/hook";
import { useConfig, useFunctionAllow } from "./queries";

// components & Utils
import { NavRoutes } from "Components/Navbar/navRoutes";
import { GetUrlPath } from "Utility/hooks/url";

// images
import OsmartNavLogo from "../../image/OsmartNavLogo.png";
import { setChatWorkNotify, setTeamPlusNotify } from "Components/Management/ManagementSlice";
import { setDeleteNode } from "Components/Dashboard/dashboardSlice";

const { Sider } = Layout;
// antd ui 自訂義布局
const { useBreakpoint } = Grid;

type MenuItem = Required<MenuProps>["items"][number];

// 用於格式化 MCSV 版本號碼，接收一個 MCSV 字串，然後從該字串中提取特定位置的字符，組合成可讀的版本號。
// 返回一個格式化後的版本號字符串。
const versionFormatter = (MCSV: string | undefined): string => {
  if (!MCSV) {
    return "";
  }
  // 處理版本號碼格式化
  const readableVersion = `v${MCSV.substring(7, 8)}.${MCSV.substring(8, 9)}.${MCSV.substring(9, 10)}-${parseInt(
    MCSV.substring(10),
    16
  )}`;
  return readableVersion;
};

const Nav: React.FC = React.memo(() => {
  const pathname = GetUrlPath().split("/")[1];
  const screens = useBreakpoint();
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  // 從 Redux global slice中取得 帳戶權限等級、 登入狀態
  const { level, isLogin } = useAppSelector((state) => state.global);
  const { data: config } = useConfig();
  const { data: functionAllowData, isSuccess: success } = useFunctionAllow();
  const MCSV = config?.MCSV;
  const disp = useAppDispatch();

  useEffect(() => {
    if (success) {
      if (functionAllowData?.team_plus !== undefined) {
        disp(setTeamPlusNotify(functionAllowData.team_plus));
      }
      if (functionAllowData?.chatwork !== undefined) {
        disp(setChatWorkNotify(functionAllowData.chatwork));
      }
      if (functionAllowData?.delete_node !== undefined) {
        disp(setDeleteNode(functionAllowData?.delete_node));
      }
    }
  }, [functionAllowData, success]);

  // console.log("versionFormatter",versionFormatter(MCSV))

  // useEffect(() => {
  //   console.log("NavRoutes", NavRoutes);
  // }, []);
  /*items
    根據用戶的權限層級（level）以及導航項目的配置（NavRoutes）和權限數據（functionAllowData）。
    如果條件滿足，則創建相應的導航項目，否則返回 undefined。*/

  // @ts-ignore
  const items: MenuItem[] =
    success &&
    NavRoutes.map((item) => {
      if (item.level.includes(level)) {
        if (
          functionAllowData?.hasOwnProperty(item.title.toLowerCase()) &&
          !functionAllowData?.[item.title.toLowerCase()]
        ) {
          return;
        } else {
          return getItem(item.title, item.path, item.icon);
        }
      } else {
        return;
      }
    });

  // console.log("screens", screens);
  const showHamberger = screens.xs;
  // 點擊事件：當點擊到 Navbar 任意個 key 時 帶入路由跳轉
  // 例如 : path /management
  const onClick: MenuProps["onClick"] = ({ key: path }) => {
    // console.log("path", path);
    if (path.includes("http")) {
      window.open(path, "_blank", "noopener,noreferrer");
    } else {
      navigate(path);
    }
  };

  return pathname === "osmartGo" || pathname === "osmartGO" ? (
    <></>
  ) : isLogin ? (
    <Fragment>
      {showHamberger && (
        <Button
          onClick={() => setCollapsed((prev) => !prev)}
          className={styles.trigger}
          icon={<MenuUnfoldOutlined style={{ fontSize: "24px", lineHeight: "40px" }} />}
        ></Button>
      )}

      <Sider
        // 表示側邊欄是否可以被折疊
        collapsible
        // 指定側邊欄的折疊狀態
        collapsed={collapsed}
        // 當側邊欄被折疊或展開時觸發的回調函數，這裡使用 setCollapsed 函數更新 collapsed 狀態
        onCollapse={(value) => setCollapsed(value)}
        className={styles.navbar}
        // 側邊欄的斷點，當屏幕寬度小於這個斷點時，側邊欄將被折疊。這裡是當屏幕寬度小於 "xl"（Extra Large）時
        breakpoint="xl"
        theme="light"
        // 側邊欄的寬度
        width={160}
        // 側邊欄在折疊狀態下的寬度，如果 showHamberger 為真（即屏幕寬度小於斷點），則將側邊欄完全隱藏（寬度為 0），否則為 80 像素
        collapsedWidth={showHamberger ? 0 : 80}
        // 指定側邊欄的觸發器（即展開/折疊的按鈕）。如果 showHamberger 為真，則使用 null（即不顯示觸發器），否則不設定觸發器。
        trigger={showHamberger && null}
      >
        {/* 側邊欄 O'smart LOGO 提示文字工具*/}
        <Tooltip
          title={
            <Fragment>
              <p>{`MCSV : ${MCSV} `}</p>
              <p>{versionFormatter(MCSV)}</p>
            </Fragment>
          }
          placement="right"
          autoAdjustOverflow
        >
          {/* 當 sidebar 收折時 顯示 小logo ，展開時 顯示 正常大小 */}
          <img src={OsmartNavLogo} className={collapsed ? styles["navbar__logo--collapsed"] : styles.navbar__logo} />
        </Tooltip>

        <Anchor bounds={5}>
          <Menu
            theme="light"
            // 用於指定當前被選中的菜單項目的 key（或 path），window.location.pathname 表示當前瀏覽器的 URL 路徑
            selectedKeys={[window.location.pathname]}
            // 指定初始預設菜單項目，使用當前瀏覽器的 URL 路徑 （或者如果為空，則使用 NavRoutes[0].path，即預設的第一個路由）
            defaultSelectedKeys={[window.location.pathname || NavRoutes[0].path]}
            // 菜單的模式，這裡是 "inline"，表示菜單將顯示為垂直顯示。
            mode="inline"
            items={items}
            onClick={onClick}
          />
        </Anchor>
      </Sider>
    </Fragment>
  ) : (
    <Fragment> </Fragment>
  );
});

export default Nav;

function getItem(label: React.ReactNode, key: React.Key, icon?: React.ReactNode, children?: MenuItem[]): MenuItem {
  return {
    key,
    icon,
    children,
    label,
  } as MenuItem;
}
